<template>
  <div class="w-full pb-10">
    <vx-card class="org-card w-full max-w-5xl mx-auto">
      <div class="p-2 md:p-4">
        
        <!-- Top Section: Logo & Basic Info -->
        <div class="flex flex-col md:flex-row gap-6 md:gap-8 mb-8">
          
          <!-- Left: Logo Upload -->
          <div class="w-full md:w-1/3 flex flex-col items-center mt-4"> <!-- Added mt-4 to move it down slightly -->
            
            <div class="profile-picture-wrapper" @click="$refs.update_avatar_input.click()">
              <vs-avatar
                v-if="logoInput"
                class="profile-picture m-0"
                size="160px"
                :src="logoInput"
              />
              <vs-avatar
                v-else-if="localData.logo"
                class="profile-picture m-0"
                size="160px"
                :src="constants.s3Bucket + '/organization/logo/' + localData.logo"
              />
              <vs-avatar
                v-else
                class="profile-picture m-0"
                size="160px"
                :src="require('@/assets/images/icons/organization/business_black_36dp.svg')"
              />
              
              <!-- Persistent Camera Badge -->
              <div class="camera-badge pointer-events-none">
                <feather-icon icon="CameraIcon" svgClasses="w-5 h-5 text-gray-700" />
              </div>
            </div>

            <!-- Description to fill vertical gap -->
            <div class="text-center mt-5 px-4 max-w-[250px]">
              <p class="text-sm font-semibold text-gray-800 mb-1">Organization Logo</p>
              <p class="text-xs text-gray-500 leading-relaxed">
                Click to upload. We recommend a 1:1 aspect ratio.<br>
                Max size: 1MB (JPG, PNG)
              </p>
            </div>

            <!-- Hidden Input & Cropper Prompt -->
            <vs-prompt
              title="Adjust Organization Logo"
              accept-text="Save"
              @cancel="closeEditLogoPrompt"
              @accept="cropAndUploadLogo"
              @close="closeEditLogoPrompt"
              :active.sync="editLogoPrompt"
            >
              <div class="con-exemple-prompt">
                <clipper-basic
                  v-if="editLogoPrompt"
                  :src="tempLogo"
                  :ratio="1"
                  :init-width="100"
                  :init-height="100"
                  ref="clipper"
                ></clipper-basic>
              </div>
            </vs-prompt>
            <input
              type="file"
              class="hidden"
              name="Profile Picture"
              ref="update_avatar_input"
              @change="openEditLogoPrompt"
              accept="image/*"
            />
          </div>

          <!-- Right: Basic Info Inputs -->
          <div class="w-full md:w-2/3">
            <vs-row class="mb-5">
              <label>Organization Name</label>
              <vs-input class="w-full mt-1" v-model="localData.name" />
            </vs-row>
            
            <vs-row class="mb-5">
              <label>Organization Username</label>
              
              <div v-if="!orgData.slug" class="w-full mt-1">
                <vs-input class="w-full" v-model="localData.slug" @input="checkSlug()" />
                <span v-if="localData.slug" class="mt-1 block">
                  <small v-if="usernameError" class="text-danger">{{ usernameError }}</small>
                  <small v-else-if="usernameAvailable" class="text-success">{{ usernameAvailable }}</small>
                </span>
              </div>
              
              <div v-else class="w-full mt-1">
                <vs-input class="w-full" v-model="localData.slug" disabled />
                <small class="block mt-1 text-gray-500">Please contact support for changing the username</small>
              </div>
            </vs-row>
            
            <vs-row class="mb-5">
              <label>Website</label>
              <vs-input class="w-full mt-1" v-model="localData.website" />
            </vs-row>
            
            <vs-row class="mb-5">
              <label>Location</label>
              <vs-input class="w-full mt-1" v-model="localData.location" />
            </vs-row>
          </div>
        </div>

        <!-- Middle Section: Theme Colors -->
        <div class="flex flex-col md:flex-row gap-6 md:gap-8 mb-8">
          
          <div class="w-full md:w-1/2">
            <label>Primary Theme Color</label>
            <div class="flex flex-wrap gap-2 mt-3">
              <div
                v-for="(cs, index) in colorSet"
                :key="index"
                class="colorset-item flex items-center justify-center relative shadow-sm"
                @click.stop="setPrimaryThemeColor(cs)"
                :style="{ backgroundColor: cs }"
              >
                <feather-icon
                  v-if="localData.primaryThemeColor === cs"
                  icon="CheckIcon"
                  svgClasses="text-white w-5 h-5 drop-shadow-md"
                />
              </div>
            </div>
          </div>

          <div class="w-full md:w-1/2">
            <label>Secondary Theme Color</label>
            <vx-input-group class="w-full mt-3">
              <template slot="prepend">
                <div class="prepend-text bg-primary">
                  <span>#</span>
                </div>
              </template>
              <vs-input class="w-full" v-model="localData.secondaryThemeColor" />
            </vx-input-group>
          </div>
        </div>

        <!-- Bottom Section: About -->
        <div class="vx-row mb-8">
          <div class="vx-col w-full">
            <vs-row>
              <label class="mb-2 block">About</label>
              <vs-textarea
                class="w-full"
                counter="200"
                :counter-danger.sync="counterDanger"
                maxlength="200"
                v-model="localData.about"
              />
            </vs-row>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex flex-wrap items-center justify-between pt-4 mt-2 border-t border-gray-100">
          <vs-button
            color="#f63d68"
            class="px-4 font-semibold"
            :style="{
              color: '#F63D68 !important',
              background: '#F63D68' + '1a !important',
            }"
            @click="deleteOrgPrompt = true"
            icon-pack="feather"
            icon="icon-trash-2"
          >
            Delete Organization
          </vs-button>

          <vs-button
            color="#44CC88"
            class="px-6 font-semibold"
            :disabled="!validForm"
            @click.stop="saveOrganizationProfile()"
          >
            Save Changes
          </vs-button>
        </div>

        <!-- Remove relationship popup  -->
        <vs-prompt
          title="Do you want to delete this organization?"
          @close="deleteOrgPrompt = false"
          :active="deleteOrgPrompt"
          buttons-hidden
          color="#000"
        >
          <div class="flex flex-wrap items-center justify-end gap-4 mt-4">
            <vs-button
              color="#fff"
              class="px-4 py-2 text-slate-900 border-slate-800 border border-solid font-semibold"
              @click="deleteOrgPrompt = false"
            >
              Cancel
            </vs-button>

            <vs-button
              color="#f63d68"
              class="px-6 py-2 font-semibold"
              @click="deleteOrg"
            >
              Delete
            </vs-button>
          </div>
        </vs-prompt>
        
      </div>
    </vx-card>
  </div>
</template>

<script>
import {
  successNotifyService,
  failureNotifyService,
} from "@/services/errors.service.js";
import constants from "@/constants.js";
import vSelect from "vue-select";

export default {
  data() {
    return {
      constants,
      counterDanger: false,
      localData: {},
      colorSet: [
        "#3BA0BE",
        "#6859B4",
        "#008000",
        "#B3B3B3",
        "#C6211B",
        "#E4D033",
        "#EA7526",
        "#F36677",
      ],
      logoInput: null,
      usernameError: false,
      usernameAvailable: false,
      editLogoPrompt: false,
      tempLogo: null,
      initialImgInput: null,
      newLogoUrl: null,
      organizationTypeOptions: ["Business", "Academic", "Recruiter"],
      orgPrivacyTypes: [
        {
          value: "PUBLIC",
          name: "Public",
        },
        {
          value: "PRIVATE",
          name: "Private",
        },
        {
          value: "SECRET",
          name: "Secret",
        },
        {
          value: "TEST",
          name: "Test",
        },
      ],
      deleteOrgPrompt: false,
    };
  },
  components: { vSelect },
  computed: {
    isUserLoggedIn() {
      return this.$store.state.auth.isUserLoggedIn;
    },
    userData() {
      return this.$store.state.users.data;
    },
    userId() {
      return this.$store.state.auth.user._id;
    },
    orgData() {
      return this.$store.state.organization.orgData;
    },
    validForm() {
      if (this.usernameError) return false;
      else return true;
    },
  },
  watch: {
    orgData(newVal) {
      if (newVal) {
        this.localData = JSON.parse(JSON.stringify(this.orgData));
      }
    },
  },
  methods: {
    checkSlug() {
      this.$store
        .dispatch("organization/checkSlug", this.localData.slug)
        .then((res) => {
          if (res.total > 0) {
            this.usernameError = "This username already exists";
          } else {
            this.usernameError = false;
            this.usernameAvailable = "This username is available";
          }
        })
        .catch((err) => {});
    },
    updateOrganizationLogo(input) {
      const payload = {
        imgInput: input,
        _id: this.userId,
        maxSize: 1, // In MB
      };

      const _id = payload._id;

      const logoData = input.target.result;
      const logoUrl = _id + "-" + this.initialImgInput.target.files[0].name;

      const olObj = {
        logoData,
        logoUrl,
      };

      this.logoInput = logoData;

      this.localData.olObj = olObj;
      this.localData.logo = logoUrl;
    },
    setPrimaryThemeColor(cs) {
      this.localData["primaryThemeColor"] = cs;
      this.$forceUpdate();
    },
    saveOrganizationProfile() {
      this.$vs.loading();
      const payload = {
        _id: this.orgData._id,
        name: this.localData.name,
        website: this.localData.website,
        logo: this.localData.logo,
        olObj: this.localData.olObj,
        workEmail: this.localData.workEmail,
        slug: this.localData.slug ? this.localData.slug : null,
        organizationType: this.localData.organizationType,
        location: this.localData.location,
        primaryThemeColor: this.localData.primaryThemeColor,
        secondaryThemeColor: this.localData.secondaryThemeColor,
        organizationCulture: this.localData.organizationCulture,
        about: this.localData.about,
        organizationPrivacy: this.localData.organizationPrivacy,
      };

      this.$store
        .dispatch("organization/updateOrganization", payload)
        .then(() => {
          this.$vs.loading.close();
          this.usernameError = false;
          this.usernameAvailable = false;
          this.$vs.notify({
            title: "Organization account updated successfully",
            color: "success",
          });

          setTimeout(() => {
            window.location.reload();
          }, 500);
        })
        .catch((err) => {
          this.$vs.loading.close();
          this.$vs.notify({
            title: "Error updating organization account",
            color: "danger",
          });

          throw err;
        });
    },
    closeEditLogoPrompt() {
      this.$forceUpdate();
    },
    logoValidations(imgInput, maxSize) {
      if (imgInput.target.files && imgInput.target.files[0]) {
        let currentThis = this;

        if (imgInput.target.files > 1) {
          return;
        }
        const parts = imgInput.target.files[0].name.split(".");
        const ext = parts[parts.length - 1];

        if (ext !== "jpg" && ext !== "jpeg" && ext !== "png") {
          const payload = {
            title: "Incorrect File Type",
            text: "Please upload a supported image file type, e.g. jpg, png, etc.",
            currentThis,
          };

          failureNotifyService(payload);

          return;
        }

        if (imgInput.target.files[0].size / (1024 * 1024) > maxSize) {
          const payload = {
            title: "Image File Size Exceeded",
            text: "The size of the profile picture should be less than 1MB.",
            currentThis,
          };

          failureNotifyService(payload);

          return;
        }

        return true;
      }
    },
    openEditLogoPrompt(input) {
      if (!this.logoValidations(input, 1)) return;

      this.initialImgInput = input;

      const reader = new FileReader();
      reader.onload = (e) => {
        this.tempLogo = e.target.result;
        this.editLogoPrompt = true;
      };
      reader.readAsDataURL(input.target.files[0]);
    },
    cropAndUploadLogo() {
      const currentThis = this;
      const canvas = this.$refs.clipper.clip();
      canvas.toBlob(function (blob) {
        const reader = new FileReader();
        reader.onload = (e) => {
          currentThis.updateOrganizationLogo(e);
        };
        reader.readAsDataURL(blob);
      });
    },
    deleteOrg() {
      this.$store
        .dispatch("organization/deleteOrganization", this.orgData._id)
        .then((res) => {
          this.$vs.notify({
            title: "Organization account deleted successfully",
            color: "success",
          });

          setTimeout(() => {
            this.$router.push("/user/my-organizations");
          }, 500);
        })
        .catch((err) => {
          this.$vs.notify({
            title: "Error in deleting organization account. Please try again .",
            color: "danger",
          });

          throw err;
        })
        .finally(() => {
          this.deleteOrgPrompt = false;
        });
    },
  },
  mounted() {
    if (this.orgData) {
      this.localData = JSON.parse(JSON.stringify(this.orgData));
    }
  },
  created() {
    if (this.isUserLoggedIn) {
      if (Object.keys(this.userData).length) {
        this.localData = JSON.parse(JSON.stringify(this.orgData));
      }
    }
  },
};
</script>

<style scoped>
/* Reset aligns */
* {
  text-align: left;
}
.text-center {
  text-align: center;
}

/* Custom Upload Avatar */
.profile-picture-wrapper {
  position: relative;
  width: 160px;
  height: 160px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  border-radius: 50%;
  border: 1px solid #ebedf0;
  background-color: #f8f9fa;
}

.profile-picture {
  position: relative;
  width: 160px !important;
  height: 160px !important;
}

/* Persistent Camera Badge styling */
.camera-badge {
  position: absolute;
  bottom: 5px;
  right: 5px;
  background-color: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  transition: transform 0.2s, background-color 0.2s;
  z-index: 10;
}

.profile-picture-wrapper:hover .camera-badge {
  background-color: #f3f4f6;
  transform: scale(1.05);
}

.profile-picture-wrapper:hover {
  opacity: 0.9;
}

/* Color Selection Boxes */
.colorset-item {
  width: 42px;
  height: 42px;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s ease;
}
.colorset-item:hover {
  transform: translateY(-2px);
}

/* Keep Vuesax textarea sizing normal */
::v-deep .vs-textarea {
  background: #fbfbfc;
  border-radius: 6px;
  height: 130px !important;
  color: #888888;
}

::v-deep .vs-input--input {
  background: #fbfbfc !important;
}
</style>